import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.jsx';
import LoginPage from './pages/LoginPage.jsx';
import SocialCallbackPage from './pages/SocialCallbackPage.jsx';
import ClientPortal from './pages/ClientPortal.jsx';
import AttorneyPortal from './pages/AttorneyPortal.jsx';
import AdminPortal from './pages/AdminPortal.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import QboApp from './pages/QboApp.jsx';
import QboDisconnected from './pages/QboDisconnected.jsx';
import JetAssistant from './components/jet/JetAssistant.jsx';

/** Clients/admins use Settings inside the shared client chrome. */
function SettingsRoute() {
  const { user } = useAuth();
  const location = useLocation();
  const role = String(user?.role || 'CLIENT').toUpperCase();
  if (role === 'CLIENT' || role === 'ADMIN') {
    return (
      <Navigate
        to={`/client/settings${location.search || ''}${location.hash || ''}`}
        replace
      />
    );
  }
  return <SettingsPage />;
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="boot-screen">
        <div className="boot-mark" aria-hidden />
        <p>Loading DemandJet…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={defaultRouteForRole(user.role)} replace />;
  }
  return children;
}

function defaultRouteForRole(role) {
  if (role === 'ATTORNEY') return '/attorney';
  if (role === 'ADMIN') return '/admin';
  return '/client/home';
}

export default function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="boot-screen">
        <div className="boot-mark" aria-hidden />
        <p>Loading DemandJet…</p>
      </div>
    );
  }

  return (
    <>
      <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to={defaultRouteForRole(user.role)} replace /> : <LoginPage />}
      />
      <Route path="/auth/social/callback" element={<SocialCallbackPage />} />
      {/* QuickBooks Apps-tab mini app — Intuit SSO, no DemandJet password */}
      <Route path="/qbo" element={<QboApp />} />
      <Route path="/qbo/disconnected" element={<QboDisconnected />} />
      <Route
        path="/client/*"
        element={
          <Protected roles={['CLIENT', 'ADMIN']}>
            <ClientPortal />
          </Protected>
        }
      />
      <Route
        path="/attorney/*"
        element={
          <Protected roles={['ATTORNEY', 'ADMIN']}>
            <AttorneyPortal />
          </Protected>
        }
      />
      <Route
        path="/admin/*"
        element={
          <Protected roles={['ADMIN']}>
            <AdminPortal />
          </Protected>
        }
      />
      <Route
        path="/settings/*"
        element={
          <Protected roles={['CLIENT', 'ATTORNEY', 'ADMIN']}>
            <SettingsRoute />
          </Protected>
        }
      />
      <Route
        path="*"
        element={<Navigate to={user ? defaultRouteForRole(user.role) : '/login'} replace />}
      />
      </Routes>
      {user ? <JetAssistant /> : null}
    </>
  );
}
